/**
 * Ask to void an unmatched bank line (a duplicate capture, a statement correction).
 *
 * Voiding says the line was never real money, so it leaves the queue and the cash proof
 * and cannot be undone. It takes two people: this form asks, and a manager other than
 * the requester confirms it (`VoidRequestPanel`). A wire that is already matched to a
 * deposit is refused by the server (reject or reverse the deposit instead).
 */
import { useState } from 'react';
import reconciliationService from '../../services/reconciliation.service';
import type { BankTransaction } from '../../services/bankTransactions.service';
import { colors } from '../../styles/theme';
import { VOID_REASON_MAX, voidConfirmation, voidProblem, submitVoid } from './bankRecon.helpers';

const inputStyle = { background: colors.canvas, color: colors.ink, border: `1px solid ${colors.border}` };

export default function VoidLinePanel({ tx, onVoided }: Readonly<{
  tx: Pick<BankTransaction, 'id' | 'amount' | 'currency_code'>;
  onVoided: (message: string) => void;
}>): JSX.Element {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const result = await submitVoid(reconciliationService, tx.id, reason);
    setBusy(false);
    if (result.ok) {
      onVoided(result.message);
    } else {
      setConfirming(false);
      setError(result.error);
    }
  };

  return (
    <VoidLineForm
      idPrefix={tx.id}
      tx={tx}
      reason={reason}
      confirming={confirming}
      busy={busy}
      error={error}
      onReason={setReason}
      onReview={() => setConfirming(true)}
      onBack={() => setConfirming(false)}
      onConfirm={() => { void submit(); }}
    />
  );
}

export function VoidLineForm({
  idPrefix, tx, reason, confirming, busy, error, onReason, onReview, onBack, onConfirm,
}: Readonly<{
  idPrefix: string;
  tx: Pick<BankTransaction, 'amount' | 'currency_code'>;
  reason: string;
  confirming: boolean;
  busy: boolean;
  error: string | null;
  onReason: (reason: string) => void;
  onReview: () => void;
  onBack: () => void;
  onConfirm: () => void;
}>): JSX.Element {
  const problem = voidProblem(reason);
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
      {error && (
        <div className="rounded-lg p-2 mb-3 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      <label htmlFor={`${idPrefix}-void-reason`} className="block text-xs font-semibold mb-1"
             style={{ color: colors.inkSoft }}>
        Why is this line not real money?
      </label>
      <textarea id={`${idPrefix}-void-reason`} rows={2} value={reason} disabled={busy || confirming}
                onChange={(e) => onReason(e.target.value.slice(0, VOID_REASON_MAX))}
                className="w-full px-3 py-2 rounded-lg text-sm mb-2" style={inputStyle} />
      {confirming ? (
        <div className="rounded-lg p-3" style={{ border: `1px solid ${colors.warning}` }}>
          <p className="text-sm mb-3" style={{ color: colors.ink }}>{voidConfirmation(tx)}</p>
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={onConfirm}
                    className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                    style={{ background: colors.danger, color: '#FFFFFF' }}>
              {busy ? 'Asking…' : 'Yes, ask to void this line'}
            </button>
            <button type="button" disabled={busy} onClick={onBack}
                    className="px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              Go back
            </button>
          </div>
        </div>
      ) : (
        <>
          {problem && <p className="text-xs mb-2" style={{ color: colors.warning }}>{problem}</p>}
          <button type="button" disabled={problem !== null || busy} onClick={onReview}
                  className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                  style={{ background: colors.navy, color: '#FFFFFF' }}>
            Review before asking
          </button>
        </>
      )}
    </div>
  );
}
